import { useCallback, useEffect, useState } from 'react';
import {
  organisation,
  type City,
  type Country,
  type District,
  type Location,
  type LocationType,
  type Region,
  type SaveLocation,
} from '../api/organisation';
import {
  IconCircleAlert,
  IconMapPin,
  IconPencil,
  IconPlus,
  IconRefreshCw,
  IconSearchX,
  IconTrash2,
  IconTriangleAlert,
} from '../icons';
import { CODE_MAX, matches, tn } from '../i18n';
import { describeError, useTranslate } from '../i18n/react';
import { Badge } from '../ui/Badge';
import { Button, IconButton } from '../ui/Button';
import { ConfirmDialog } from '../ui/ConfirmDialog';
import { Drawer } from '../ui/Drawer';
import { EmptyState } from '../ui/EmptyState';
import { Alert, Field, SearchInput, Select, Textarea, TextInput, Toggle } from '../ui/Field';
import { Card, PageHeader, Toolbar } from '../ui/Page';
import { RowMenu } from '../ui/RowMenu';
import { Table, type Column } from '../ui/Table';

/**
 * The locations — the stores, branches and dealer outlets that get audited.
 *
 * <p>Fifteen fields, so this one gets a real form rather than an editable row. Three of them
 * are not in an ordinary store record and matter here: the coordinates are what check-in
 * distance is measured against, the location type is what decides which checklist applies,
 * and the floor area is what makes a finding comparable between a small street shop and a
 * large one.</p>
 *
 * <p>Only the code and the name are required. A customer's first afternoon is spent getting
 * a list of stores in from whatever they already have, and refusing one for want of a floor
 * area would stop that afternoon dead.</p>
 */

interface Lists {
  types: LocationType[];
  regions: Region[];
  countries: Country[];
  cities: City[];
  districts: District[];
}

const blank: SaveLocation = {
  code: '',
  name: '',
  locationTypeId: null,
  regionId: null,
  countryId: null,
  cityId: null,
  districtId: null,
  address: null,
  phone: null,
  latitude: null,
  longitude: null,
  areaSquareMetres: null,
  openedOn: null,
  description: null,
  isActive: true,
};

const toDraft = (l: Location): SaveLocation => ({
  code: l.code,
  name: l.name,
  locationTypeId: l.locationTypeId,
  regionId: l.regionId,
  countryId: l.countryId,
  cityId: l.cityId,
  districtId: l.districtId,
  address: l.address,
  phone: l.phone,
  latitude: l.latitude,
  longitude: l.longitude,
  areaSquareMetres: l.areaSquareMetres,
  openedOn: l.openedOn,
  description: l.description,
  isActive: l.isActive,
});

/** Empty means empty, not zero: a blank floor area is unknown, and zero is a claim. */
const number = (text: string): number | null => {
  const trimmed = text.trim();
  if (trimmed === '') return null;
  const value = Number(trimmed.replace(',', '.'));
  return Number.isFinite(value) ? value : null;
};

/**
 * Digits with the usual separators, at least seven of them. Loose on purpose — stores write
 * "0212 555 12 34", "+90 (212) 555-1234" and extensions — but "yok" or an e-mail address is
 * not a phone number (field test, 2 November).
 */
export const phoneValid = (phone: string | null): boolean =>
  !phone || (/^\+?[\d\s().\-/]+$/.test(phone) && (phone.match(/\d/g) ?? []).length >= 7);

/**
 * A number typed as text, kept as text while it is being typed.
 *
 * <p>The box used to show the parsed number back: "40." became 40 the moment the dot went
 * in, so "40.98" arrived as 4098 and "41,5" as 415 — a coordinate or an area with a decimal
 * could not be typed at all, only pasted (field test, 2 November). It also swallowed
 * letters without a word; now it keeps them and says the value is not a number.</p>
 */
function DecimalInput({
  id,
  placeholder,
  value,
  onChange,
}: {
  id: string;
  placeholder: string;
  value: number | null;
  onChange: (value: number | null) => void;
}) {
  const t = useTranslate();
  const [typed, setTyped] = useState(value === null ? '' : String(value));

  // A new record, or the drawer reopened on another one: follow the value, unless what is
  // typed already means it.
  useEffect(() => {
    setTyped((current) => (number(current) === value ? current : value === null ? '' : String(value)));
  }, [value]);

  const invalid = typed.trim() !== '' && number(typed) === null;

  return (
    <>
      <TextInput
        id={id}
        inputMode="decimal"
        placeholder={placeholder}
        aria-invalid={invalid || undefined}
        value={typed}
        onChange={(e) => {
          setTyped(e.target.value);
          onChange(number(e.target.value));
        }}
      />
      {invalid ? <span className="nv-field__error" role="alert">{t('locations.notANumber')}</span> : null}
    </>
  );
}

const text = (value: string): string | null => (value.trim() === '' ? null : value.trim());

export function Locations() {
  const t = useTranslate();
  const [rows, setRows] = useState<Location[]>([]);
  const [lists, setLists] = useState<Lists>({
    types: [],
    regions: [],
    countries: [],
    cities: [],
    districts: [],
  });
  const [regionFilter, setRegionFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [term, setTerm] = useState('');
  const [draft, setDraft] = useState<{ id: string | null; values: SaveLocation } | null>(null);
  const [doomed, setDoomed] = useState<Location | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const [panelError, setPanelError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [list, types, regions, countries, cities, districts] = await Promise.all([
        organisation.locations.list({
          regionId: regionFilter || undefined,
          locationTypeId: typeFilter || undefined,
        }),
        organisation.locationTypes.list(),
        organisation.regions.list(),
        organisation.countries.list(),
        organisation.cities.list(),
        organisation.districts.list(),
      ]);
      setRows(list);
      setLists({ types, regions, countries, cities, districts });
      setFailed(false);
      setError(null);
    } catch (e) {
      setFailed(true);
      setError(describeError(e, 'error.listFailed'));
    } finally {
      setLoading(false);
    }
  }, [regionFilter, typeFilter]);

  useEffect(() => {
    void reload();
  }, [reload]);

  async function save() {
    if (!draft || busy) return;
    setBusy(true);
    try {
      const body = { ...draft.values, code: draft.values.code.trim(), name: draft.values.name.trim() };
      if (draft.id) await organisation.locations.update(draft.id, body);
      else await organisation.locations.create(body);
      setDraft(null);
      setPanelError(null);
      await reload();
    } catch (e) {
      setPanelError(describeError(e, 'error.saveFailed'));
    } finally {
      setBusy(false);
    }
  }

  async function remove(row: Location) {
    setBusy(true);
    try {
      await organisation.locations.remove(row.id);
      setError(null);
      await reload();
    } catch (e) {
      setError(describeError(e, 'error.deleteFailed'));
    } finally {
      setBusy(false);
    }
  }

  const values = draft?.values;
  const set = (change: Partial<SaveLocation>) =>
    setDraft((d) => (d ? { ...d, values: { ...d.values, ...change } } : d));

  // The address narrows as it goes: cities in the chosen country, districts in the chosen
  // city. Picking a country that the current city is not in clears the levels below it,
  // because the server refuses that pair anyway and finding out at save time is worse.
  const cities = values?.countryId
    ? lists.cities.filter((c) => c.countryId === values.countryId)
    : [];
  const districts = values?.cityId ? lists.districts.filter((d) => d.cityId === values.cityId) : [];

  // Everything that holds Save, said at once beside it. It used to be greyed out with no
  // word why, and the reasons surfaced one by one (field test, 2 November).
  const missing = values
    ? [
        !values.code.trim() && t('common.code'),
        !values.name.trim() && t('common.name'),
        !phoneValid(values.phone) && t('locations.phone'),
      ].filter((x): x is string => Boolean(x))
    : [];
  const canSave = Boolean(values) && missing.length === 0;

  // The server filters by region and type; the name and code are searched here, over what it
  // returned, because an auditor looking for "metropol" wants it found as they type.
  const shown = term.trim() ? rows.filter((row) => matches(`${row.code} ${row.name}`, term)) : rows;

  const columns: Column<Location>[] = [
    {
      id: 'code',
      header: t('common.code'),
      width: '1%',
      cell: (row) => <span className="nv-code">{row.code}</span>,
      skeletonWidth: '48px',
    },
    {
      id: 'name',
      header: t('common.name'),
      className: 'is-primary',
      cell: (row) => row.name,
      skeletonWidth: '64%',
    },
    {
      id: 'type',
      header: t('locations.type'),
      cell: (row) =>
        row.locationTypeName ?? (
          // Without it the field app cannot narrow the template list, so this gap is worth
          // showing rather than leaving blank.
          <Badge tone="na" icon={IconTriangleAlert}>{t('locations.noType')}</Badge>
        ),
      skeletonWidth: '70%',
    },
    {
      id: 'region',
      header: t('locations.region'),
      className: 'is-muted',
      cell: (row) =>
        row.regionName ? (
          <>
            {row.regionName}
            {row.operationName ? <div className="nv-hint">{row.operationName}</div> : null}
          </>
        ) : (
          <span className="nv-subtle">{t('common.none')}</span>
        ),
      skeletonWidth: '60%',
    },
    {
      id: 'city',
      header: t('locations.city'),
      className: 'is-muted',
      cell: (row) =>
        row.cityName ? (
          <>
            {row.cityName}
            {row.districtName ? <div className="nv-hint">{row.districtName}</div> : null}
          </>
        ) : (
          <span className="nv-subtle">{t('common.none')}</span>
        ),
      skeletonWidth: '56%',
    },
    {
      id: 'coordinates',
      header: t('locations.coordinates'),
      cell: (row) =>
        row.latitude !== null && row.longitude !== null ? (
          <span className="nv-code">{`${row.latitude.toFixed(4)}, ${row.longitude.toFixed(4)}`}</span>
        ) : (
          // Check-in distance is measured against these. Missing means the system takes
          // "the auditor was at the store" on trust.
          <Badge tone="na" icon={IconTriangleAlert}>{t('locations.noCoordinates')}</Badge>
        ),
      skeletonWidth: '112px',
    },
    {
      id: 'status',
      header: t('common.status'),
      cell: (row) => (
        <Badge tone={row.isActive ? 'comment' : 'neutral'}>
          {row.isActive ? t('common.active') : t('common.inactive')}
        </Badge>
      ),
      skeletonWidth: '62px',
    },
  ];

  function open(row: Location) {
    setPanelError(null);
    setDraft({ id: row.id, values: toDraft(row) });
  }

  function openNew() {
    setPanelError(null);
    setDraft({ id: null, values: { ...blank, regionId: regionFilter || null } });
  }

  return (
    <>
      <PageHeader
        crumbs={[t('app.organisation'), t('nav.locations')]}
        title={t('locations.title')}
        subtitle={t('locations.subtitle')}
        action={
          <Button variant="primary" icon={IconPlus} onClick={openNew}>{t('locations.add')}</Button>
        }
      />

      {error ? <Alert>{error}</Alert> : null}

      <Card className="nv-table-card">
        <Toolbar count={tn('common.shown', shown.length)}>
          <SearchInput value={term} onValueChange={setTerm} placeholder={t('locations.search')} />

          <Field label={t('locations.region')}>
            {({ id }) => (
              <Select id={id} value={regionFilter} onChange={(e) => setRegionFilter(e.target.value)}>
                <option value="">{t('common.all')}</option>
                {lists.regions.map((r) => (
                  <option key={r.id} value={r.id}>{r.name}</option>
                ))}
              </Select>
            )}
          </Field>

          <Field label={t('locations.type')}>
            {({ id }) => (
              <Select id={id} value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
                <option value="">{t('common.all')}</option>
                {lists.types.map((x) => (
                  <option key={x.id} value={x.id}>{x.name}</option>
                ))}
              </Select>
            )}
          </Field>
        </Toolbar>

        <Table
          caption={t('locations.title')}
          columns={columns}
          rows={shown}
          getRowId={(row) => row.id}
          status={loading ? 'loading' : failed ? 'error' : 'ready'}
          onRowClick={open}
          rowActions={(row) => (
            <>
              <IconButton label={t('common.edit')} icon={IconPencil} onClick={() => open(row)} />
              <RowMenu
                label={t('common.moreActions')}
                items={[
                  {
                    label:
                      row.auditCount > 0
                        ? t('locations.auditedCannotDelete', { count: row.auditCount })
                        : t('common.delete'),
                    icon: IconTrash2,
                    danger: true,
                    disabled: busy || row.auditCount > 0,
                    onSelect: () => setDoomed(row),
                  },
                ]}
              />
            </>
          )}
          errorState={
            <EmptyState
              icon={IconCircleAlert}
              tone="danger"
              title={t('common.loadFailed')}
              text={t('common.loadFailedText')}
              action={<Button icon={IconRefreshCw} onClick={() => void reload()}>{t('common.retry')}</Button>}
            />
          }
          emptyState={
            term || regionFilter || typeFilter ? (
              <EmptyState
                icon={IconSearchX}
                title={t('common.noResults', { term: term || t('common.all') })}
                text={t('common.noResultsText')}
                action={
                  <Button
                    onClick={() => {
                      setTerm('');
                      setRegionFilter('');
                      setTypeFilter('');
                    }}
                  >
                    {t('common.clearFilters')}
                  </Button>
                }
              />
            ) : (
              <EmptyState
                icon={IconMapPin}
                title={t('locations.empty')}
                text={t('locations.emptyText')}
                action={<Button variant="primary" icon={IconPlus} onClick={openNew}>{t('locations.add')}</Button>}
              />
            )
          }
        />
      </Card>

      <Drawer
        open={draft !== null}
        title={t(draft?.id ? 'locations.editTitle' : 'locations.newTitle')}
        onClose={() => setDraft(null)}
        footer={
          <>
            {missing.length > 0 ? (
              <span className="nv-hint">{t('locations.missing', { fields: missing.join(', ') })}</span>
            ) : null}
            <Button onClick={() => setDraft(null)}>{t('common.cancel')}</Button>
            <Button
              variant="primary"
              disabled={!canSave || busy}
              loading={busy}
              loadingText={t('common.saving')}
              onClick={() => void save()}
            >
              {t('common.save')}
            </Button>
          </>
        }
      >
        {draft && values ? (
          <>
            <div className="nv-form-pair">
              <Field label={t('common.code')} required>
                {({ id }) => (
                  <TextInput id={id} placeholder="S95" maxLength={CODE_MAX} value={values.code} onChange={(e) => set({ code: e.target.value })} />
                )}
              </Field>

              <Field label={t('common.name')} required>
                {({ id }) => (
                  <TextInput id={id} value={values.name} onChange={(e) => set({ name: e.target.value })} />
                )}
              </Field>
            </div>

            <div className="nv-drawer__section">
              <h3 className="nv-h3">{t('locations.whoAnswers')}</h3>
              <p className="nv-hint">{t('locations.whoAnswersHint')}</p>

              <div className="nv-form-pair">
                <Field label={t('locations.type')}>
                  {({ id }) => (
                    <Select
                      id={id}
                      value={values.locationTypeId ?? ''}
                      onChange={(e) => set({ locationTypeId: e.target.value || null })}
                    >
                      <option value="">{t('common.none')}</option>
                      {lists.types.map((x) => (
                        <option key={x.id} value={x.id}>{x.name}</option>
                      ))}
                    </Select>
                  )}
                </Field>

                <Field label={t('locations.region')}>
                  {({ id }) => (
                    <Select
                      id={id}
                      value={values.regionId ?? ''}
                      onChange={(e) => set({ regionId: e.target.value || null })}
                    >
                      <option value="">{t('common.none')}</option>
                      {lists.regions.map((r) => (
                        <option key={r.id} value={r.id}>{`${r.name} · ${r.operationName}`}</option>
                      ))}
                    </Select>
                  )}
                </Field>
              </div>
            </div>

            <div className="nv-drawer__section">
              <h3 className="nv-h3">{t('locations.whereItStands')}</h3>

              <div className="nv-form-grid">
                <Field label={t('locations.country')}>
                  {({ id }) => (
                    <Select
                      id={id}
                      value={values.countryId ?? ''}
                      onChange={(e) => set({ countryId: e.target.value || null, cityId: null, districtId: null })}
                    >
                      <option value="">{t('common.none')}</option>
                      {lists.countries.map((c) => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                    </Select>
                  )}
                </Field>

                <Field label={t('locations.city')}>
                  {({ id }) => (
                    <Select
                      id={id}
                      value={values.cityId ?? ''}
                      disabled={!values.countryId}
                      onChange={(e) => set({ cityId: e.target.value || null, districtId: null })}
                    >
                      <option value="">{t('common.none')}</option>
                      {cities.map((c) => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                    </Select>
                  )}
                </Field>

                <Field label={t('locations.district')}>
                  {({ id }) => (
                    <Select
                      id={id}
                      value={values.districtId ?? ''}
                      disabled={!values.cityId}
                      onChange={(e) => set({ districtId: e.target.value || null })}
                    >
                      <option value="">{t('common.none')}</option>
                      {districts.map((d) => (
                        <option key={d.id} value={d.id}>{d.name}</option>
                      ))}
                    </Select>
                  )}
                </Field>
              </div>

              <Field label={t('locations.address')}>
                {({ id }) => (
                  <Textarea
                    id={id}
                    placeholder={t('common.optional')}
                    value={values.address ?? ''}
                    onChange={(e) => set({ address: text(e.target.value) })}
                  />
                )}
              </Field>
            </div>

            <div className="nv-drawer__section">
              <h3 className="nv-h3">{t('locations.checkIn')}</h3>
              <p className="nv-hint">{t('locations.checkInHint')}</p>

              <div className="nv-form-pair">
                <Field label={t('locations.latitude')}>
                  {({ id }) => (
                    <DecimalInput id={id} placeholder="40.9819" value={values.latitude} onChange={(latitude) => set({ latitude })} />
                  )}
                </Field>

                <Field label={t('locations.longitude')}>
                  {({ id }) => (
                    <DecimalInput id={id} placeholder="29.0254" value={values.longitude} onChange={(longitude) => set({ longitude })} />
                  )}
                </Field>
              </div>
            </div>

            <div className="nv-form-grid">
              <Field label={t('locations.area')}>
                {({ id }) => (
                  <DecimalInput id={id} placeholder="412" value={values.areaSquareMetres} onChange={(areaSquareMetres) => set({ areaSquareMetres })} />
                )}
              </Field>

              <Field label={t('locations.phone')} error={phoneValid(values.phone) ? null : t('locations.notAPhone')}>
                {({ id, describedBy, invalid }) => (
                  <TextInput
                    id={id}
                    inputMode="tel"
                    aria-describedby={describedBy}
                    aria-invalid={invalid || undefined}
                    value={values.phone ?? ''}
                    onChange={(e) => set({ phone: text(e.target.value) })}
                  />
                )}
              </Field>

              <Field label={t('locations.openedOn')}>
                {({ id }) => (
                  <TextInput
                    id={id}
                    type="date"
                    value={values.openedOn ?? ''}
                    onChange={(e) => set({ openedOn: e.target.value || null })}
                  />
                )}
              </Field>
            </div>

            <Field label={t('common.description')}>
              {({ id }) => (
                <Textarea
                  id={id}
                  placeholder={t('common.optional')}
                  value={values.description ?? ''}
                  onChange={(e) => set({ description: text(e.target.value) })}
                />
              )}
            </Field>

            <div className="nv-drawer__section">
              <Toggle
                checked={values.isActive}
                onCheckedChange={(isActive) => set({ isActive })}
                label={t('locations.isActive')}
              />
              <p className="nv-hint">{t('locations.isActiveHint')}</p>
            </div>

            {panelError ? <Alert>{panelError}</Alert> : null}
          </>
        ) : null}
      </Drawer>

      <ConfirmDialog
        open={doomed !== null}
        title={doomed ? t('common.deleteTitle', { name: `${doomed.code} ${doomed.name}` }) : ''}
        confirmLabel={t('common.delete')}
        onConfirm={() => (doomed ? remove(doomed) : Promise.resolve())}
        onClose={() => setDoomed(null)}
      >
        <p>{t('common.deleteWarning')}</p>
      </ConfirmDialog>
    </>
  );
}
